class PhoneBanner extends HTMLElement {
  constructor() {
    super();
    this.wrapper = this.querySelector('.banner-wrapper');
    if (!this.wrapper) return;
    
    this.items = Array.from(this.querySelectorAll('.ultimate-slider-item'));
    if (this.items.length <= 1) return;
    
    this.currentIndex = 0;
    this.isScrolling = false;
    this.isTransitioning = false;
    this.touchStartX = 0;
    this.touchEndX = 0;
    this.scrollTimeout = null;
    this.initialized = false;
    this.originalItemCount = this.items.length;
    this.autoPlayInterval = null;
    this.isUserInteracting = false;
    
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      setTimeout(() => this.init(), 100);
    }
  }

  init() {
    if (this.initialized || this.items.length <= 1) return;
    this.initialized = true;
    
    this.setupInfiniteScroll();
    this.setupTouchGestures();
    this.setupScrollDetection();
    this.setupAutoPlay();
  }

  setupInfiniteScroll() {
    const firstItem = this.items[0].cloneNode(true);
    const lastItem = this.items[this.items.length - 1].cloneNode(true);
    
    this.wrapper.insertBefore(lastItem, this.items[0]);
    this.wrapper.appendChild(firstItem);
    
    this.items = Array.from(this.querySelectorAll('.ultimate-slider-item'));
    this.currentIndex = 1;
    
    setTimeout(() => {
      const itemWidth = this.wrapper.offsetWidth;
      if (itemWidth > 0) {
        this.wrapper.scrollLeft = this.currentIndex * itemWidth;
      }
    }, 50);
  }

  setupTouchGestures() {
    let isDragging = false;
    let startScrollLeft = 0;
    
    this.wrapper.addEventListener('touchstart', (e) => {
      if (this.isTransitioning) return;
      this.pauseAutoPlay();
      this.touchStartX = e.touches[0].clientX;
      startScrollLeft = this.wrapper.scrollLeft;
      isDragging = true;
    }, { passive: true });

    this.wrapper.addEventListener('touchmove', (e) => {
      if (!isDragging) return;
      this.touchEndX = e.touches[0].clientX;
    }, { passive: true });

    this.wrapper.addEventListener('touchend', () => {
      if (!isDragging) return;
      isDragging = false;
      
      const deltaX = this.touchStartX - this.touchEndX;
      const threshold = 50;

      if (Math.abs(deltaX) > threshold && !this.isTransitioning) {
        if (deltaX > 0) {
          this.next();
        } else {
          this.prev();
        }
      }
      
      this.touchStartX = 0;
      this.touchEndX = 0;
      this.resumeAutoPlay();
    }, { passive: true });
  }

  setupScrollDetection() {
    let rafId = null;
    
    this.wrapper.addEventListener('scroll', () => {
      if (this.isTransitioning) return;
      
      this.pauseAutoPlay();
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        this.checkInfiniteLoop();
      });
      
      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = setTimeout(() => {
        this.handleScrollEnd();
        this.resumeAutoPlay();
      }, 150);
    }, { passive: true });
  }

  checkInfiniteLoop() {
    if (this.isTransitioning || this.items.length <= 1) return;
    
    const scrollLeft = this.wrapper.scrollLeft;
    const itemWidth = this.wrapper.offsetWidth;
    if (itemWidth === 0) return;
    
    const maxScroll = (this.items.length - 1) * itemWidth;
    const tolerance = 5;
    
    if (scrollLeft <= tolerance) {
      this.isTransitioning = true;
      this.wrapper.style.scrollBehavior = 'auto';
      this.currentIndex = this.items.length - 2;
      this.wrapper.scrollLeft = this.currentIndex * itemWidth;
      requestAnimationFrame(() => {
        this.wrapper.style.scrollBehavior = 'smooth';
        this.isTransitioning = false;
      });
    } else if (scrollLeft >= maxScroll - tolerance) {
      this.isTransitioning = true;
      this.wrapper.style.scrollBehavior = 'auto';
      this.currentIndex = 1;
      this.wrapper.scrollLeft = this.currentIndex * itemWidth;
      requestAnimationFrame(() => {
        this.wrapper.style.scrollBehavior = 'smooth';
        this.isTransitioning = false;
      });
    } else {
      this.currentIndex = Math.round(scrollLeft / itemWidth);
    }
  }

  handleScrollEnd() {
    if (this.isTransitioning || this.items.length <= 1) return;
    
    const scrollLeft = this.wrapper.scrollLeft;
    const itemWidth = this.wrapper.offsetWidth;
    const currentIndex = Math.round(scrollLeft / itemWidth);
    this.currentIndex = currentIndex;
  }

  scrollToIndex(index, smooth = true) {
    if (index < 0 || index >= this.items.length || !this.wrapper) return;
    
    const itemWidth = this.wrapper.offsetWidth;
    if (itemWidth === 0) return;
    
    const scrollPosition = index * itemWidth;
    
    if (smooth) {
      this.isScrolling = true;
      this.wrapper.scrollTo({
        left: scrollPosition,
        behavior: 'smooth'
      });
      setTimeout(() => {
        this.isScrolling = false;
        this.checkInfiniteLoop();
      }, 600);
    } else {
      this.wrapper.scrollLeft = scrollPosition;
    }
  }

  next() {
    if (this.isScrolling || this.isTransitioning || this.items.length <= 1) return;
    
    this.isScrolling = true;
    this.currentIndex++;
    
    if (this.currentIndex >= this.items.length - 1) {
      this.scrollToIndex(this.currentIndex, true);
      setTimeout(() => {
        this.isTransitioning = true;
        this.wrapper.style.scrollBehavior = 'auto';
        this.currentIndex = 1;
        this.wrapper.scrollLeft = this.currentIndex * this.wrapper.offsetWidth;
        requestAnimationFrame(() => {
          this.wrapper.style.scrollBehavior = 'smooth';
          this.isTransitioning = false;
          this.isScrolling = false;
        });
      }, 600);
    } else {
      this.scrollToIndex(this.currentIndex, true);
    }
  }

  prev() {
    if (this.isScrolling || this.isTransitioning || this.items.length <= 1) return;
    
    this.isScrolling = true;
    this.currentIndex--;
    
    if (this.currentIndex <= 0) {
      this.scrollToIndex(this.currentIndex, true);
      setTimeout(() => {
        this.isTransitioning = true;
        this.wrapper.style.scrollBehavior = 'auto';
        this.currentIndex = this.items.length - 2;
        this.wrapper.scrollLeft = this.currentIndex * this.wrapper.offsetWidth;
        requestAnimationFrame(() => {
          this.wrapper.style.scrollBehavior = 'smooth';
          this.isTransitioning = false;
          this.isScrolling = false;
        });
      }, 600);
    } else {
      this.scrollToIndex(this.currentIndex, true);
    }
  }

  setupAutoPlay() {
    if (this.items.length <= 1) return;
    
    this.autoPlayInterval = setInterval(() => {
      if (!this.isUserInteracting && !this.isScrolling && !this.isTransitioning) {
        this.next();
      }
    }, 7000);
  }

  pauseAutoPlay() {
    this.isUserInteracting = true;
    if (this.autoPlayInterval) {
      clearInterval(this.autoPlayInterval);
      this.autoPlayInterval = null;
    }
  }

  resumeAutoPlay() {
    this.isUserInteracting = false;
    if (!this.autoPlayInterval && this.items.length > 1) {
      this.autoPlayInterval = setInterval(() => {
        if (!this.isUserInteracting && !this.isScrolling && !this.isTransitioning) {
          this.next();
        }
      }, 7000);
    }
  }

  disconnectedCallback() {
    if (this.autoPlayInterval) {
      clearInterval(this.autoPlayInterval);
    }
  }
}

if (!customElements.get('phone-banner')) {
  customElements.define('phone-banner', PhoneBanner);
}

